import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {getByKeyPath} from 'Quoting/utils/utilities';
import {flow} from 'fp-ts/lib/function';
import {List as IList, Map as IMap, fromJS} from 'immutable';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {createGIM2BenefitStructure} from '../../PlanTemplates/HI/GIM2';
import {
  getPlanConfigurationOptionsWithRestriction,
  getSelectedBenefitOption,
} from '../../PlanTemplates/HI/planOptionConfiguration';
import {getDailyHospitalConfinementPropertyPaths} from './GHI3/propertyPaths';
import {
  getHosAdmissionDetailsPaths,
  getHosConfinementDetailsPaths,
  getIcuConfinementDetailsPaths,
} from './propertyPaths';

const HosAdmissionBenefitAmountErrorKey = 'HIHosAdmissionBenefit.benefitAmount';
const HIConfinementOptionSelectionKey = 'HIConfinementOptionSelection';

/* Start: Confinement Benefits */
const FirstDayHospitalConfinement = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const propertyPaths = getHosAdmissionDetailsPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'first-day-hospital-confinement-row-1'},
      items: [
        {
          key: propertyPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              id={propertyPaths.benefitAmount}
              value={getQuoteFieldValue(propertyPaths.benefitAmount)}
              onChange={e => {
                if (!isNaN(Number(e.target.value))) {
                  updateQuoteFieldValue(propertyPaths.benefitAmount, Number(e.target.value));
                }
              }}
              adornment="money"
              required
              error={Boolean(quoteFormErrors?.[HosAdmissionBenefitAmountErrorKey])}
              helperText={quoteFormErrors ? quoteFormErrors?.[HosAdmissionBenefitAmountErrorKey] : undefined}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.firstDayConfinementPaymentFreqLimitCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosAdmissionBenefit.firstDayConfinementPaymentFreqLimitCd"
              label="Payment Frequency Limit"
              id={propertyPaths.firstDayConfinementPaymentFreqLimitCd}
              value={getQuoteFieldValue(propertyPaths.firstDayConfinementPaymentFreqLimitCd)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.firstDayConfinementPaymentFreqLimitCd, e.target.value as string)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'first-day-hospital-confinement-row-2'},
      items: [
        {
          key: propertyPaths.newbornInitialConfinementPct,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Newborn Child Initial Confinement (% Payable)"
              type="number"
              id={propertyPaths.newbornInitialConfinementPct}
              value={getQuoteFieldValue(propertyPaths.newbornInitialConfinementPct)}
              onChange={e => updateQuoteFieldValue(propertyPaths.newbornInitialConfinementPct, Number(e.target.value))}
              adornment="percentage"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.normalPregnancyCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosAdmissionBenefit.normalPregnancyCd"
              label="Normal Pregnancy"
              id={propertyPaths.normalPregnancyCd}
              value={getQuoteFieldValue(propertyPaths.normalPregnancyCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.normalPregnancyCd, e.target.value as string)}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'first-day-hospital-confinement-row-3'},
      items: [
        {
          key: 'complications-from-pregnancy',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosAdmissionBenefit.complicationsFromPregnancyCd"
              label="Complications From Pregnancy"
              id={propertyPaths.complicationsFromPregnancyCd}
              value={getQuoteFieldValue(propertyPaths.complicationsFromPregnancyCd)}
              onChange={e =>
                updateQuoteFieldValue(propertyPaths.complicationsFromPregnancyCd, e.target.value as string)
              }
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const HIHosConfinementBenefitAmountErrorKey = 'HIHosConfinementBenefit.benefitAmount';

const DailyHospitalConfinement = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const propertyPaths = getHosConfinementDetailsPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'daily-hospital-confinement-row-1'},
      items: [
        {
          key: propertyPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              id={propertyPaths.benefitAmount}
              value={getQuoteFieldValue(propertyPaths.benefitAmount)}
              onChange={e => updateQuoteFieldValue(propertyPaths.benefitAmount, Number(e.target.value))}
              adornment="money"
              required
              error={Boolean(quoteFormErrors?.[HIHosConfinementBenefitAmountErrorKey])}
              helperText={quoteFormErrors ? quoteFormErrors?.[HIHosConfinementBenefitAmountErrorKey] : undefined}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosConfinementBenefit.maxBenefitNumber"
              label="Max # Days Limit (Per Confinement)"
              id={propertyPaths.maxBenefitNumber}
              value={getQuoteFieldValue(propertyPaths.maxBenefitNumber)}
              onChange={e => updateQuoteFieldValue(propertyPaths.maxBenefitNumber, Number(e.target.value))}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'daily-hospital-confinement-row-2'},
      items: [
        {
          key: propertyPaths.newbornConfinementPct,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Newborn Child Daily Confinement (% Payable)"
              type="number"
              id={propertyPaths.newbornConfinementPct}
              value={getQuoteFieldValue(propertyPaths.newbornConfinementPct)}
              onChange={e => updateQuoteFieldValue(propertyPaths.newbornConfinementPct, Number(e.target.value))}
              adornment="percentage"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const HIICUConfinementBenefitDailyBenefitAmountErrorKey = 'HIICUConfinementBenefit.dailyBenefitAmount';

export const HospitalIntensiveCare = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const propertyPaths = getIcuConfinementDetailsPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'hospital-intensive-care-row-1'},
      items: [
        {
          key: propertyPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Daily Benefit Amount"
              type="number"
              id={propertyPaths.benefitAmount}
              value={getQuoteFieldValue(propertyPaths.benefitAmount)}
              onChange={e => updateQuoteFieldValue(propertyPaths.benefitAmount, Number(e.target.value))}
              adornment="money"
              required
              error={Boolean(quoteFormErrors?.[HIICUConfinementBenefitDailyBenefitAmountErrorKey])}
              helperText={
                quoteFormErrors ? quoteFormErrors?.[HIICUConfinementBenefitDailyBenefitAmountErrorKey] : undefined
              }
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: propertyPaths.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIICUConfinementBenefit.maxBenefitNumber"
              label="Max # Days Limit (Per Confinement)"
              id={propertyPaths.maxBenefitNumber}
              value={getQuoteFieldValue(propertyPaths.maxBenefitNumber)}
              onChange={e => updateQuoteFieldValue(propertyPaths.maxBenefitNumber, Number(e.target.value))}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const FirstDayHospitalIntensiveCare = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'fist-day-hospital-intensive-care-row-1'},
      items: [
        {
          key: 'daily-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Daily Benefit Amount"
              value={0}
              type="number"
              id={'daily-benefit-amount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'payment-frequency-limit',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Payment Frequency Limit"
              options={[]}
              value={''}
              id={'payment-frequency-limit'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const ConfinementBenefitOptionSelection = () => {
  const {selectedPlan, quoteFormErrors, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);

  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();

  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';

  const plans = getQuoteFieldValue('benefitPackagingDetail.plans') as IList<IMap<string, any>>;

  const selectedAmounts: IList<{
    hosAdmission: number;
    hosConfinement: number;
    icuConfinement: number;
  }> = IList.isList(plans)
    ? plans?.map(plan => ({
        hosAdmission: getByKeyPath(
          plan,
          'coverageDefs[0].benefitStructure.hosAdmissionDetails.benefitAmount.amount'
        ) as number,
        hosConfinement: getByKeyPath(
          plan,
          'coverageDefs[0].benefitStructure.hosConfinementDetails.benefitAmount.amount'
        ) as number,
        icuConfinement:
          (getByKeyPath(
            plan,
            'coverageDefs[0].benefitStructure.icuConfinementDetails.benefitAmount.amount'
          ) as number) ?? 0,
      }))
    : IList();

  const gim2ConfinementBenefitOptions = getPlanConfigurationOptionsWithRestriction(
    'GIM2',
    situsStateCd,
    selectedAmounts,
    selectedPlan?.planIndex ?? 0,
    [0, 3] // restrict option1 and option4 combination
  );

  const hosAdmissionPropertyPaths = getHosAdmissionDetailsPaths(selectedPlan?.planIndex);

  const hosConfinementPropertyPaths = getDailyHospitalConfinementPropertyPaths(selectedPlan?.planIndex);

  const icuConfinementPropertyPaths = getIcuConfinementDetailsPaths(selectedPlan?.planIndex);

  const selectedCoverageIndex = 0;

  const planRootId =
    getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]._key.rootId`) ?? '';

  const coverageDefId =
    getQuoteFieldValue(
      `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}].coverageDefs[${selectedCoverageIndex}]._key.id`
    ) ?? '';

  const [selectedConfinementBenefitOption, setSelectedConfinementBenefitOption] = useState<any>(() =>
    getSelectedBenefitOption(
      'GIM2',
      {
        hosAdmission: getQuoteFieldValue(hosAdmissionPropertyPaths.benefitAmount),
        hosConfinement: getQuoteFieldValue(hosConfinementPropertyPaths.benefitAmount),
        icuConfinement: getQuoteFieldValue(icuConfinementPropertyPaths.benefitAmount),
      },
      situsStateCd
    )
  );

  const handleConfinementBenefitsOptionSelection = (value: any) => {
    setSelectedConfinementBenefitOption(value);

    let confinementFields = [
      {
        path: hosAdmissionPropertyPaths.benefitAmount,
        newValue: value.hosAdmission,
      },
      {
        path: hosConfinementPropertyPaths.benefitAmount,
        newValue: value.hosConfinement,
      },
    ];
    const icuConfinementFields = getICUConfinementBenefitDetails(
      value.icuConfinement,
      defaultPlanValues.get('GIM2') ?? IMap<string, any>(),
      planRootId,
      coverageDefId,
      selectedPlan?.planIndex,
      selectedCoverageIndex
    );

    confinementFields = confinementFields.concat(icuConfinementFields);

    updateQuoteFieldValues(confinementFields);
  };

  const rows: GridRowProps[] = [
    {
      rowProps: {key: `gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`},
      items: [
        {
          key: `gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Plan Options"
              placeholder="Please select the required plan option"
              id={`gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`}
              value={selectedConfinementBenefitOption}
              options={gim2ConfinementBenefitOptions}
              onChange={e => handleConfinementBenefitsOptionSelection(e.target.value)}
              required
              error={Boolean(quoteFormErrors?.[HIConfinementOptionSelectionKey])}
              helperText={quoteFormErrors ? quoteFormErrors?.[HIConfinementOptionSelectionKey] : undefined}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const ConfinementBenefitsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const uniqueKeyForOptions = `${getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.id`
  )}-${selectedPlan?.planIndex}`;

  const selectedCoverageIndex = 0;

  const pathToBenefitStructure = () =>
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex ??
      0}].coverageDefs[${selectedCoverageIndex}].benefitStructure`;

  const pathToBenefitAppliedBoolean = (benefitApplicabilityIndicator: string) =>
    `${pathToBenefitStructure()}.${benefitApplicabilityIndicator}`;

  const getIsHIBenefitApplied = flow(pathToBenefitAppliedBoolean, getQuoteFieldValue, Boolean);

  const isHosAdmissionApplied = getIsHIBenefitApplied('isHosAdmissionApplied');
  const isHosConfinementApplied = getIsHIBenefitApplied('isHosConfinementApplied');
  const isICUConfinementApplied = getIsHIBenefitApplied('isICUConfinementApplied');
  const isFirstDayICUConfinementApplied = getIsHIBenefitApplied(
    'isFirstDayHospitalIntensiveCareConfinementBenefitApplied'
  );

  const icuAmount = getQuoteFieldValue(getIcuConfinementDetailsPaths().benefitAmount);

  const isICUDisabled = isNaN(Number(icuAmount)) || Number(icuAmount) === 0;

  const confinementBenefits: ConditionalSection[] = [
    {
      Component: FirstDayHospitalConfinement,
      key: 'FirstDayHospitalConfinement',
      label: 'First Day Hospital Confinement "Admissions"',
      isApplied: isHosAdmissionApplied,
      disabled: true,
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(pathToBenefitAppliedBoolean('isHosAdmissionApplied'), visibility),
    },
    {
      Component: DailyHospitalConfinement,
      key: 'DailyHospitalConfinement',
      label: 'Daily Hospital Confinement',
      isApplied: isHosConfinementApplied,
      disabled: isHosConfinementApplied,
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(pathToBenefitAppliedBoolean('isHosConfinementApplied'), visibility),
    },
    {
      Component: HospitalIntensiveCare,
      key: 'HospitalIntensiveCare',
      label: 'Hospital Intensive Care',
      isApplied: isICUConfinementApplied,
      hidden: isICUDisabled,
      disabled: true,
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(pathToBenefitAppliedBoolean('isICUConfinementApplied'), visibility),
    },
    {
      Component: FirstDayHospitalIntensiveCare,
      key: 'FirstDayHospitalIntensiveCare',
      label: 'First Day Hospital Intensive Care',
      isApplied: isFirstDayICUConfinementApplied,
      disabled: true,
      hidden: true,
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(
          pathToBenefitAppliedBoolean('isFirstDayHospitalIntensiveCareConfinementBenefitApplied'),
          visibility
        ),
    },
  ];

  return (
    <FormWrapper title="Confinement Benefits" collapsible withSeparator expanded>
      <Grid direction="column">
        <ConfinementBenefitOptionSelection key={uniqueKeyForOptions} />
        <QuoteFormConditionalSection sections={confinementBenefits} />
      </Grid>
    </FormWrapper>
  );
};

const getICUConfinementBenefitDetails = (
  amountValue: number,
  defaultValues: IMap<string, any>,
  planRootId: string,
  coverageDefId: string,
  selectedPlanIndex: number = 0,
  selectedCoverageIndex: number = 0
) => {
  const icuConfinementDetailsPaths = getIcuConfinementDetailsPaths(selectedPlanIndex, selectedCoverageIndex);

  if (amountValue === 0) {
    return [
      {
        path: icuConfinementDetailsPaths.icuConfinementDetails,
        newValue: undefined,
      },
      {
        path: icuConfinementDetailsPaths.isICUConfinementApplied,
        newValue: false,
      },
    ];
  }

  const benefitStructure = createGIM2BenefitStructure(defaultValues, planRootId, coverageDefId);

  const defaultIcuConfinementDetails = [
    {
      path: icuConfinementDetailsPaths.icuConfinementDetails,
      newValue: fromJS({
        ...benefitStructure.icuConfinementDetails,
        benefitAmount: {_type: 'Money', amount: amountValue, currency: 'USD'},
      }),
    },
    {
      path: icuConfinementDetailsPaths.isICUConfinementApplied,
      newValue: true,
    },
  ];

  return defaultIcuConfinementDetails;
};
